import { useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Plus, X } from "lucide-react";
import { toast } from "sonner";
import { useShowById } from "../hooks/useShows";
import { useMergedShow } from "../hooks/useMergedShow";
import { TOAST_MS } from "../lib/constants";
import { submitCreditSuggestion } from "../lib/services";
import { formatApiError } from "../lib/api";
import { lower } from "../lib/strings";

export default function SuggestCredits() {
  const { id } = useParams();
  const navigate = useNavigate();
  const baseShow = useShowById(id);
  const merged = useMergedShow(id);

  const start = merged || baseShow;

  const [playwright, setPlaywright] = useState(start?.playwright || "");
  const [director, setDirector] = useState(start?.director || "");
  const [company, setCompany] = useState(start?.company || "");
  const [theatre, setTheatre] = useState(start?.theatre || "");
  const [venue, setVenue] = useState(start?.venue || "");
  const [sourceUrl, setSourceUrl] = useState(start?.sourceUrl || "");
  const [sourceNote, setSourceNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [cast, setCast] = useState(
    start?.cast?.length
      ? start.cast.map((value, i) => ({ id: `c-init-${i}`, value }))
      : [{ id: `c-${Date.now()}`, value: "" }]
  );
  const [creativeTeam, setCreativeTeam] = useState(
    start?.creativeTeam?.length
      ? start.creativeTeam.map((c, i) => ({ id: `t-init-${i}`, role: c.role || "", name: c.name || "" }))
      : [{ id: `t-${Date.now()}`, role: "", name: "" }]
  );

  const cleanedCast = useMemo(
    () => cast.map((c) => c.value.trim()).filter(Boolean),
    [cast]
  );
  const cleanedTeam = useMemo(
    () =>
      creativeTeam
        .filter((c) => c.role.trim() || c.name.trim())
        .map((c) => ({ role: c.role.trim(), name: c.name.trim() })),
    [creativeTeam]
  );

  if (!baseShow) {
    return (
      <main className="min-h-screen bg-black text-white p-8 pb-28">
        <div className="font-display text-3xl">show not found.</div>
      </main>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    // build a payload of only non-empty CHANGED fields
    const payload = {};
    const setIfSet = (k, v) => {
      const t = (v || "").trim();
      if (t && t !== (start?.[k] || "")) payload[k] = t;
    };
    setIfSet("playwright", playwright);
    setIfSet("director", director);
    setIfSet("company", company);
    setIfSet("theatre", theatre);
    setIfSet("venue", venue);
    setIfSet("sourceUrl", sourceUrl);
    if (sourceNote.trim()) payload.sourceNote = sourceNote.trim();
    if (cleanedCast.length) payload.cast = cleanedCast;
    if (cleanedTeam.length) payload.creativeTeam = cleanedTeam;

    if (Object.keys(payload).length === 0) {
      toast("nothing changed yet");
      return;
    }
    setSubmitting(true);
    try {
      await submitCreditSuggestion(id, payload);
      toast("submitted for review", { duration: TOAST_MS.NORMAL });
      navigate(`/show/${id}`);
    } catch (err) {
      toast(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="suggest-credits-page" className="min-h-screen bg-black text-white pb-[calc(10rem+env(safe-area-inset-bottom))]">
      <header className="px-5 pt-6 pb-4 border-b-2 border-white/15">
        <Link
          to={`/show/${id}`}
          className="font-mono text-xs lowercase text-white/60 hover:text-white inline-flex items-center gap-1.5"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> back
        </Link>
        <h1 className="font-display text-3xl sm:text-4xl leading-tight mt-3">
          suggest credits<span className="text-acid">.</span>
        </h1>
        <div className="font-mono text-[11px] text-white/50 mt-2">
          {lower(baseShow.title)} · {lower(baseShow.theatre)}
        </div>
      </header>

      <form onSubmit={submit} className="px-5 py-6 space-y-5">
        <Field label="playwright / creator" testid="suggest-playwright" value={playwright} onChange={setPlaywright} />
        <Field label="director" testid="suggest-director" value={director} onChange={setDirector} />
        <Field label="company" testid="suggest-company" value={company} onChange={setCompany} />
        <Field label="theatre" testid="suggest-theatre" value={theatre} onChange={setTheatre} />
        <Field label="venue" testid="suggest-venue" value={venue} onChange={setVenue} />
        <Field label="source url" testid="suggest-source" value={sourceUrl} onChange={setSourceUrl} placeholder="https://" />

        {/* cast */}
        <section data-testid="suggest-cast">
          <div className="flex items-center justify-between mb-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
              cast
            </label>
            <button
              type="button"
              data-testid="suggest-cast-add"
              onClick={() => setCast((c) => [...c, { id: `c-${Date.now()}-${c.length}`, value: "" }])}
              className="font-mono text-[10px] uppercase tracking-wider text-acid inline-flex items-center gap-1"
            >
              <Plus className="w-3 h-3" strokeWidth={2} /> add row
            </button>
          </div>
          <ul className="space-y-2">
            {cast.map((row, i) => (
              <li key={row.id} className="flex gap-2">
                <input
                  data-testid={`suggest-cast-${i}`}
                  value={row.value}
                  onChange={(e) =>
                    setCast((c) => c.map((x) => (x.id === row.id ? { ...x, value: e.target.value } : x)))
                  }
                  placeholder="cast member name"
                  className="flex-1 bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm placeholder:text-white/30 focus:border-acid outline-none"
                />
                <button
                  type="button"
                  data-testid={`suggest-cast-remove-${i}`}
                  onClick={() => setCast((c) => c.filter((x) => x.id !== row.id))}
                  className="p-2 text-white/40 hover:text-acid"
                  aria-label="remove cast row"
                >
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* creative team */}
        <section data-testid="suggest-team">
          <div className="flex items-center justify-between mb-2">
            <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
              creative team
            </label>
            <button
              type="button"
              data-testid="suggest-team-add"
              onClick={() => setCreativeTeam((c) => [...c, { id: `t-${Date.now()}-${c.length}`, role: "", name: "" }])}
              className="font-mono text-[10px] uppercase tracking-wider text-acid inline-flex items-center gap-1"
            >
              <Plus className="w-3 h-3" strokeWidth={2} /> add row
            </button>
          </div>
          <ul className="space-y-2">
            {creativeTeam.map((row, i) => (
              <li key={row.id} className="grid grid-cols-[1fr_1fr_auto] gap-2">
                <input
                  data-testid={`suggest-team-role-${i}`}
                  value={row.role}
                  onChange={(e) =>
                    setCreativeTeam((c) =>
                      c.map((x) => (x.id === row.id ? { ...x, role: e.target.value } : x))
                    )
                  }
                  placeholder="role"
                  className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm placeholder:text-white/30 focus:border-acid outline-none"
                />
                <input
                  data-testid={`suggest-team-name-${i}`}
                  value={row.name}
                  onChange={(e) =>
                    setCreativeTeam((c) =>
                      c.map((x) => (x.id === row.id ? { ...x, name: e.target.value } : x))
                    )
                  }
                  placeholder="name"
                  className="bg-transparent border-2 border-white/30 px-3 py-2 font-mono text-sm placeholder:text-white/30 focus:border-acid outline-none"
                />
                <button
                  type="button"
                  data-testid={`suggest-team-remove-${i}`}
                  onClick={() => setCreativeTeam((c) => c.filter((x) => x.id !== row.id))}
                  className="p-2 text-white/40 hover:text-acid"
                  aria-label="remove team row"
                >
                  <X className="w-4 h-4" strokeWidth={2} />
                </button>
              </li>
            ))}
          </ul>
        </section>

        <div>
          <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
            where did you find this? (optional)
          </label>
          <textarea
            data-testid="suggest-source-note"
            value={sourceNote}
            onChange={(e) => setSourceNote(e.target.value)}
            rows={2}
            placeholder="e.g. show programme, theatre's website"
            className="mt-1 w-full bg-transparent border-2 border-white/30 px-3 py-2.5 font-mono text-sm placeholder:text-white/30 focus:border-acid outline-none resize-none"
          />
        </div>

        <button
          type="submit"
          data-testid="suggest-submit"
          disabled={submitting}
          className="w-full bg-acid text-black font-mono text-sm uppercase tracking-wider py-3 border-2 border-acid hover:bg-black hover:text-acid transition-colors disabled:opacity-50"
        >
          {submitting ? "submitting..." : "submit for review"}
        </button>
        <button
          type="button"
          data-testid="suggest-cancel"
          onClick={() => navigate(`/show/${id}`)}
          className="w-full font-mono text-xs uppercase tracking-wider text-white/60 hover:text-white py-2"
        >
          cancel
        </button>

        <p className="font-mono text-[10px] text-white/40 text-center leading-relaxed">
          an admin reviews suggestions before they appear on the show page.
        </p>
      </form>
    </main>
  );
}

function Field({ label, testid, value, onChange, placeholder }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
        {label}
      </label>
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1 w-full bg-transparent border-2 border-white/30 px-3 py-2.5 font-mono text-sm placeholder:text-white/30 focus:border-acid outline-none"
      />
    </div>
  );
}
